import * as THREE from "three";
import { getBuiltInFurnitureMaterial } from "./builtInFurnitureMaterials";
import { getBuiltInMaterialSurfaceMapping } from "./builtInMaterialSurfaceMappings";
import { CatalogSurfaceReference } from "../products/product3DTypes";

/**
 * SketchUp-style HSB (HSV): hue degrees, saturation/brightness percent.
 */
export type MaterialHsb = {
  h: number;
  s: number;
  b: number;
};

/**
 * Solid built-in presets only — skip local/admin albedo textures for v1.
 */
export function canEditSolidBuiltInMaterialColor(
  materialId: string | undefined,
  surfaceRef?: CatalogSurfaceReference,
): boolean {
  const builtIn = getBuiltInFurnitureMaterial(materialId ?? surfaceRef?.id);
  if (!builtIn) return false;
  if (builtIn.textureUrl || surfaceRef?.imageUrl) return false;
  const mapping = getBuiltInMaterialSurfaceMapping(builtIn.id);
  if (mapping?.imageUrl) return false;
  return true;
}

export function colorNumberToHsb(color: number): MaterialHsb {
  const { r, g, b } = new THREE.Color(color);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const s = max === 0 ? 0 : (delta / max) * 100;
  return {
    h: Math.round(h),
    s: Math.round(s),
    b: Math.round(max * 100),
  };
}

/**
 * RGB↔HSB drops hue when saturation is 0 (and sat when brightness is 0).
 * Merge preserves those channels from the previous HSB so sliders stay usable.
 */
export function mergeColorIntoHsb(
  color: number,
  previous?: MaterialHsb,
): MaterialHsb {
  const next = colorNumberToHsb(color);
  if (!previous) return next;
  if (next.b === 0) {
    return { h: previous.h, s: previous.s, b: 0 };
  }
  if (next.s === 0) {
    return { h: previous.h, s: 0, b: next.b };
  }
  return next;
}

export function hsbToColorNumber({ h, s, b }: MaterialHsb): number {
  const hue = ((h % 360) + 360) % 360;
  const saturation = clamp01(s / 100);
  const value = clamp01(b / 100);
  const chroma = value * saturation;
  const x = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = value - chroma;

  let r = 0;
  let g = 0;
  let blue = 0;
  if (hue < 60) {
    r = chroma;
    g = x;
  } else if (hue < 120) {
    r = x;
    g = chroma;
  } else if (hue < 180) {
    g = chroma;
    blue = x;
  } else if (hue < 240) {
    g = x;
    blue = chroma;
  } else if (hue < 300) {
    r = x;
    blue = chroma;
  } else {
    r = chroma;
    blue = x;
  }

  return new THREE.Color(r + m, g + m, blue + m).getHex();
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}
